// PortfolioSocialAccounts — the "Social Accounts" section on the portfolio
// (EPD-10113): a row of platform pill tabs and, for the selected platform, a
// flat "{Platform} Metrics" card grid. Stats come from channel.getSocialStats
// (EPD-10112). Themed entirely via the renderer's `--pf-*` tokens.
//
// The stat set is per-platform, following the prototype's GROUPED_ANALYTICS
// catalogs (source of truth: InsightsSection in the Creator Toolkit prototype),
// narrowed to the stats the legacy backend actually provides. With no saved
// per-platform selection yet (EPD-10133), every available stat for the platform
// shows; a stat with no value renders an em-dash rather than being dropped.
//
// Behind shared platform tabs it renders up to three sibling sub-blocks —
// Recent Posts (EPD-11833), the Social Metrics grid (EPD-10113) and Audience
// Demographics (EPD-10117) — each gated independently. With no connected
// platform, the editor surface shows per-block prompts (EPD-11833) and the
// public surface omits the section (EPD-10124).

import {
  getPlatformIcon,
  getPlatformLabel,
} from "@/components/channel/platform-utils";
import type { Platform, SocialStatsEntry } from "@/lib/queries/channel";
import {
  enabledDemographics,
  fmtCount,
  formatStat,
  isCatalogPlatform,
  visibleGridMetricsForPlatform,
  type HiddenMetricIds,
} from "@/lib/portfolio/metric-catalog";
import { useId, useRef, useState } from "react";
import {
  hasAudienceData,
  PortfolioAudienceDemographics,
} from "./portfolio-audience-demographics";
import { EmptyPrompt } from "./portfolio-empty-state";
import { hasRecentPosts, PortfolioRecentPosts } from "./portfolio-recent-posts";
import { VerifiedLabel } from "./portfolio-verified-badge";

function followerCountOf(entry: SocialStatsEntry): number | null | undefined {
  return entry.stats?.followerCount ?? entry.stats?.reach;
}

// "Last updated" label (EPD-10118) — relative for recent fetches, absolute date
// beyond a week. Returns null when there is no timestamp, so the caller can omit
// the label rather than show a misleading string.
function formatLastUpdated(iso: string | null | undefined): string | null {
  if (!iso) return null;
  const then = new Date(iso);
  if (Number.isNaN(then.getTime())) return null;
  const days = Math.floor((Date.now() - then.getTime()) / 86_400_000);
  if (days <= 0) return "Today";
  if (days === 1) return "Yesterday";
  if (days < 7) return `${days} days ago`;
  return then.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

// The block shows one tab per connected *platform*. A creator can connect more
// than one account on the same platform, so entries are collapsed to a single
// representative per platform — the account with the most followers — preserving
// first-connected order.
function representativesByPlatform(
  entries: SocialStatsEntry[],
): SocialStatsEntry[] {
  const byPlatform = new Map<string, SocialStatsEntry>();
  for (const entry of entries) {
    const current = byPlatform.get(entry.platform);
    if (
      !current ||
      (followerCountOf(entry) ?? -1) > (followerCountOf(current) ?? -1)
    ) {
      byPlatform.set(entry.platform, entry);
    }
  }
  return [...byPlatform.values()];
}

interface PortfolioSocialAccountsProps {
  entries: SocialStatsEntry[];
  // The Recent Posts (EPD-11833), Social Metrics grid (EPD-10113) and Audience
  // Demographics (EPD-10117) are independent sub-blocks sharing these tabs;
  // each is gated separately.
  showContent?: boolean;
  showStats?: boolean;
  showAudience?: boolean;
  // Per-network hidden metrics (EPD-12113). Absent → every metric shows.
  hiddenMetricIds?: HiddenMetricIds;
  // Editor shows a prompt when nothing is connected; public omits the section
  // (EPD-10124). The connect CTA deep-links to the profile's Social Accounts.
  surface?: "editor" | "public";
  channelsHref?: string;
  /** The creator's name, for the post images' alt text (EPD-12471). */
  creatorName?: string;
  /**
   * The block sits right under the header, so its first post images are
   * likely the page's largest paint and load eagerly (EPD-12468).
   */
  prioritizeImages?: boolean;
}

export function PortfolioSocialAccounts({
  entries,
  showContent = true,
  showStats = true,
  showAudience = true,
  hiddenMetricIds = {},
  surface = "public",
  channelsHref,
  creatorName,
  prioritizeImages = false,
}: PortfolioSocialAccountsProps) {
  const baseId = useId();
  const tablistRef = useRef<HTMLDivElement>(null);
  const [activePlatform, setActivePlatform] = useState<string | null>(null);

  // Nothing to render if every inner block is hidden.
  if (!showContent && !showStats && !showAudience) return null;

  const connectedEntries = entries.filter((e) => e.connected);
  // Whether a platform's Audience Demographics block is on. The four catalog
  // networks follow their Metrics Shown demographic picks. The rest have no
  // picker (and only the fixed default stat grid), so their demographics follow
  // the Social metrics toggle — otherwise nothing could hide them once the
  // Audience section toggle was retired (EPD-12034). `showAudience` still gates
  // both, so a theme migrated with Audience off keeps them hidden.
  const audienceOn = (e: SocialStatsEntry) =>
    showAudience &&
    (isCatalogPlatform(e.platform)
      ? enabledDemographics(hiddenMetricIds, e.platform).size > 0
      : showStats);
  // The metric grid is broadly available for any connected platform, so with
  // stats on every connected platform gets a tab. With stats off, though, a
  // platform only earns a tab if one of the remaining blocks has something to
  // show there — otherwise the tab opens an empty panel. Recent Posts needs a
  // thumbnailed post and demographics need audience data; filter to entries
  // satisfying an enabled block before picking a representative, so a platform
  // isn't dropped just because its highest-follower account lacks the data
  // while another account on the same platform has it.
  const tabs = showStats
    ? representativesByPlatform(connectedEntries)
    : representativesByPlatform(
        connectedEntries.filter(
          (e) =>
            (showContent && hasRecentPosts(e)) ||
            (audienceOn(e) && hasAudienceData(e)),
        ),
      );

  // No connected platform: the editor shows per-block prompts so the creator
  // knows what's missing (EPD-11833); the public page omits the section
  // entirely (EPD-10124).
  if (tabs.length === 0) {
    if (surface !== "editor") return null;
    const connectCta = channelsHref
      ? { label: "Connect social accounts", href: channelsHref }
      : undefined;
    return (
      <section className="space-y-6">
        <h2 style={{ margin: 0 }}>Social Accounts</h2>
        <div className="space-y-3">
          {showContent && (
            <EmptyPrompt
              title="Recent posts"
              message="Connect your social accounts to show your recent posts."
              cta={connectCta}
            />
          )}
          {showStats && (
            <EmptyPrompt
              title="Social stats"
              message="Connect your social accounts to show your social stats."
              cta={connectCta}
            />
          )}
          {showAudience && (
            <EmptyPrompt
              title="Audience demographics"
              message="Connect your social accounts to show audience demographics."
              cta={connectCta}
            />
          )}
        </div>
      </section>
    );
  }

  // Derive the active tab with a fallback so a stale/empty selection is safe.
  const activeIndex = Math.max(
    0,
    tabs.findIndex((t) => t.platform === activePlatform),
  );
  const active = tabs[activeIndex];
  const activePlatformKey = active.platform as Platform;
  const platformLabel = getPlatformLabel(activePlatformKey);
  const metrics = visibleGridMetricsForPlatform(
    hiddenMetricIds,
    active.platform,
  );
  const demoEnabled = isCatalogPlatform(active.platform)
    ? enabledDemographics(hiddenMetricIds, active.platform)
    : undefined;
  const lastUpdated = formatLastUpdated(active.stats?.dateLastUpdated);

  const tabId = (index: number) => `${baseId}-tab-${index}`;
  const panelId = `${baseId}-panel`;

  // Roving arrow-key selection across the tablist (WAI-ARIA tabs pattern).
  const onTabKeyDown = (e: React.KeyboardEvent, index: number) => {
    let next: number | null = null;
    if (e.key === "ArrowRight") next = (index + 1) % tabs.length;
    else if (e.key === "ArrowLeft")
      next = (index - 1 + tabs.length) % tabs.length;
    else if (e.key === "Home") next = 0;
    else if (e.key === "End") next = tabs.length - 1;
    if (next === null) return;
    e.preventDefault();
    setActivePlatform(tabs[next].platform);
    const buttons =
      tablistRef.current?.querySelectorAll<HTMLButtonElement>('[role="tab"]');
    buttons?.[next]?.focus();
  };

  return (
    <section className="space-y-6">
      <h2 style={{ margin: 0 }}>Social Accounts</h2>

      {/* Platform pill tabs — one per connected platform */}
      <div
        ref={tablistRef}
        role="tablist"
        aria-label="Platforms"
        className="flex flex-wrap gap-2"
      >
        {tabs.map((entry, index) => {
          const isActive = index === activeIndex;
          const Icon = getPlatformIcon(entry.platform as Platform);
          return (
            <button
              key={entry.platform}
              type="button"
              role="tab"
              id={tabId(index)}
              aria-selected={isActive}
              aria-controls={panelId}
              tabIndex={isActive ? 0 : -1}
              onClick={() => setActivePlatform(entry.platform)}
              onKeyDown={(e) => onTabKeyDown(e, index)}
              className="inline-flex min-w-0 max-w-full items-center gap-2 rounded-[10px] border px-3.5 py-2 text-[13px] transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              style={{
                background: isActive ? "var(--pf-surface)" : "var(--pf-bg)",
                borderColor: isActive ? "var(--pf-accent)" : "var(--pf-border)",
                color: isActive ? "var(--pf-accent)" : "var(--pf-heading)",
              }}
            >
              <Icon className="h-4 w-4 shrink-0" />
              {/* A very long handle truncates rather than widening the page;
                  the follower count stays visible. */}
              <span className="min-w-0 truncate font-medium">
                {entry.handle ?? getPlatformLabel(entry.platform as Platform)}
              </span>
              <span
                className="shrink-0 whitespace-nowrap"
                style={{ color: "var(--pf-muted)" }}
              >
                · {fmtCount(followerCountOf(entry))}
              </span>
            </button>
          );
        })}
      </div>

      {/* Panel for the active platform: the metric grid and/or the audience
          demographics block, each independently toggled. */}
      <div
        role="tabpanel"
        id={panelId}
        aria-labelledby={tabId(activeIndex)}
        tabIndex={0}
        className="space-y-8"
      >
        {showContent &&
          (surface === "editor" && !hasRecentPosts(active) ? (
            // Connected but no thumbnailed posts: the editor shows a prompt so the
            // block doesn't silently vanish, consistent with the other editor
            // empty states. The public page just renders nothing (EPD-10124).
            <EmptyPrompt
              title="Recent posts"
              message="This platform has no recent posts to show yet."
            />
          ) : (
            <PortfolioRecentPosts
              entry={active}
              creatorName={creatorName}
              prioritizeImages={prioritizeImages}
            />
          ))}

        {showStats && metrics.length > 0 && (
          <div className="space-y-3">
            {/* {Platform} Metrics — flat grid (grouping is customize-panel
                only): 2 columns on phones, 3 above the portfolio breakpoint
                (see portfolio-renderer). */}
            <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
              <h3 style={{ margin: 0 }}>{platformLabel} Metrics</h3>
              {lastUpdated && (
                <VerifiedLabel>Last updated · {lastUpdated}</VerifiedLabel>
              )}
            </div>
            <div className="grid grid-cols-2 gap-3 @[30rem]/portfolio:grid-cols-3">
              {metrics.map((metric) => (
                <div
                  key={metric.id}
                  className="min-w-0 wrap-break-word rounded-2xl border px-4 py-4 @[30rem]/portfolio:px-5"
                  style={{ borderColor: "var(--pf-border)" }}
                >
                  <div
                    className="text-[22px] font-bold leading-tight tabular-nums"
                    style={{
                      fontFamily: "var(--pf-font-heading)",
                      color: "var(--pf-heading)",
                    }}
                  >
                    {active.stats ? formatStat(metric, active.stats) : "—"}
                  </div>
                  <div
                    className="text-[12px]"
                    style={{ color: "var(--pf-muted)" }}
                  >
                    {metric.label}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {audienceOn(active) && (
          <PortfolioAudienceDemographics entry={active} enabled={demoEnabled} />
        )}
      </div>
    </section>
  );
}

// Taller than the space between the block's usual top and the fold on phones
// (823px) and laptops (940px), so the sections after it start below the fold
// and its real content arriving shifts nothing in view (EPD-12469). Measured
// blocks run 495-1838px.
const PLACEHOLDER_MIN_HEIGHT = 720;

/**
 * Holds the Social Accounts block's place while its stats load (EPD-12469).
 * Without it the block appears a few seconds after first paint and pushes the
 * sections after it down (CLS 0.16-0.45 on test portfolios). Same heading and
 * outline as the real block: platform tabs, a metric grid and a post row.
 */
export function PortfolioSocialAccountsPlaceholder() {
  const block = "animate-pulse rounded-2xl";
  const surface = { background: "var(--pf-surface)" };
  return (
    <section
      className="space-y-6"
      style={{ minHeight: PLACEHOLDER_MIN_HEIGHT }}
      aria-busy="true"
      aria-label="Social Accounts loading"
    >
      <h2 style={{ margin: 0 }}>Social Accounts</h2>
      <div className="flex gap-2">
        {[0, 1, 2].map((i) => (
          <div key={i} className={`${block} h-9 w-28`} style={surface} />
        ))}
      </div>
      <div className="grid grid-cols-2 gap-3 @[30rem]/portfolio:grid-cols-3">
        {[0, 1, 2, 3, 4, 5].map((i) => (
          <div key={i} className={`${block} h-20`} style={surface} />
        ))}
      </div>
      <div className="flex gap-2.5 overflow-hidden">
        {[0, 1, 2, 3].map((i) => (
          <div
            key={i}
            className={`${block} h-[240px] w-[180px] shrink-0`}
            style={surface}
          />
        ))}
      </div>
    </section>
  );
}
